import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { klona } from 'klona';
import { PhysicalInfoDimension, PhysicalInfoSource, TrackerCustomEvent, useTracker } from 'common-package';
import service from '@/service';
import { MotionTestCard, PainLevelRadio } from '@/components';
import PainLevelWrapper from './pain-level';
import cx from './index.module.scss';

export interface PictureContentRefAttrs {
  getPainLevelAfter: (orderId: string) => void;
  getPainLevelBefore: (orderId: string) => void;
  savePainLevel: (orderId: string, questionnaireId: number, images: string[]) => void;
}

interface PictureContentProps {
  setActionTestImgs: React.Dispatch<React.SetStateAction<any[]>>;
  currentServingOrder: GetServiceOrderListResponse;
  questionnaire: ActionTest.Questionnaire;
  handleScrollElement: VoidFunc;
  beforeHandData?: any;
  afterMassage?: boolean;
  afterWardsData?: any;
}

const PictureContent = forwardRef<PictureContentRefAttrs, PictureContentProps>(
  (
    {
      afterMassage = false,
      afterWardsData,
      setActionTestImgs,
      currentServingOrder,
      beforeHandData,
      questionnaire,
      handleScrollElement,
    },
    ref
  ) => {
    const [selectedPainLevel, setSelectedPainLevel] = useState<number>(null);
    const [selectedPainLevelByTwo, setSelectedPainLevelByTwo] = useState<PainLevelParams>({
      waistPainLevel: null,
      shoulderPainLevel: null,
    });
    const { sendEvent } = useTracker();
    const orderTypeIsCommon = useMemo(() => {
      // 人工\自定义画线\背部 显示两个疼痛等级且分部位
      if (
        currentServingOrder?.onlyManual ||
        currentServingOrder?.bodyPart === 'Back' ||
        currentServingOrder?.bodyPart === 'LowerBody' ||
        currentServingOrder?.stepFlow?.find(item => item.step === 'RoboticTask')?.subStep?.includes('CustomPalpation')
      ) {
        return false;
      }
      return true;
    }, [currentServingOrder?.bodyPart, currentServingOrder?.onlyManual, currentServingOrder?.stepFlow]);

    const isHasExtraPadding = useMemo(() => {
      if (!orderTypeIsCommon || afterMassage) {
        return true;
      }
      return false;
    }, [afterMassage, orderTypeIsCommon]);

    /** 获取理疗前的疼痛等级 */
    const getPainLevelBefore = useCallback(
      async serviceOrderId => {
        const { data } = await service.actionTest.getPainLevel({
          serviceOrderId,
        });
        if (orderTypeIsCommon) {
          if (currentServingOrder?.bodyPart === 'Shoulder') {
            setSelectedPainLevel(data?.before?.shoulderPainLevel);
          } else if (currentServingOrder?.bodyPart === 'Waist') {
            setSelectedPainLevel(data?.before?.waistPainLevel);
          }
        } else {
          setSelectedPainLevelByTwo(data?.before);
        }
      },
      [orderTypeIsCommon, currentServingOrder?.bodyPart]
    );
    /** 获取理疗后的疼痛等级 */
    const getPainLevelAfter = useCallback(
      async serviceOrderId => {
        const { data } = await service.actionTest.getPainLevel({
          serviceOrderId,
        });
        if (orderTypeIsCommon) {
          if (currentServingOrder?.bodyPart === 'Shoulder') {
            setSelectedPainLevel(data?.after?.shoulderPainLevel);
          } else if (currentServingOrder?.bodyPart === 'Waist') {
            setSelectedPainLevel(data?.after?.waistPainLevel);
          }
        } else {
          setSelectedPainLevelByTwo(data?.after);
        }
      },
      [orderTypeIsCommon, currentServingOrder?.bodyPart]
    );

    /** 保存理疗前后的疼痛等级 */
    const savePainLevel = useCallback(
      async (serviceOrderId, questionnaireId, images) => {
        let waistPainLevel, shoulderPainLevel;

        if (orderTypeIsCommon) {
          if (currentServingOrder?.bodyPart === 'Shoulder') {
            shoulderPainLevel = selectedPainLevel;
          } else if (currentServingOrder?.bodyPart === 'Waist') {
            waistPainLevel = selectedPainLevel;
          }
        } else {
          waistPainLevel = selectedPainLevelByTwo?.waistPainLevel;
          shoulderPainLevel = selectedPainLevelByTwo?.shoulderPainLevel;
        }

        await service.actionTest.saveActionTestAndPain({
          serviceOrderId,
          type: afterMassage ? 2 : 1,
          waistPainLevel,
          shoulderPainLevel,
          questionnaireId,
          images,
        });
        if (
          !!selectedPainLevel ||
          !!selectedPainLevelByTwo?.waistPainLevel ||
          !!selectedPainLevelByTwo?.shoulderPainLevel
        ) {
          sendEvent(TrackerCustomEvent.FillInPhysicalInfoSource, {
            et: 'CLK',
            c1: PhysicalInfoDimension['painLevel'],
            c2: afterMassage
              ? PhysicalInfoSource['physiotherapy-record-after']
              : PhysicalInfoSource['physiotherapy-record-before'],
          });
        }
      },
      [
        orderTypeIsCommon,
        sendEvent,
        afterMassage,
        selectedPainLevel,
        currentServingOrder?.bodyPart,
        selectedPainLevelByTwo?.waistPainLevel,
        selectedPainLevelByTwo?.shoulderPainLevel,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        getPainLevelAfter: (orderId: string) => getPainLevelAfter(orderId),
        getPainLevelBefore: (orderId: string) => getPainLevelBefore(orderId),
        savePainLevel: (orderId: string, questionnaireId: number, images: string[]) =>
          savePainLevel(orderId, questionnaireId, images),
      }),
      [getPainLevelAfter, getPainLevelBefore, savePainLevel]
    );

    return (
      <div className={cx('shockwave-action-test', { 'extra-padding': isHasExtraPadding })}>
        {orderTypeIsCommon ? (
          <PainLevelWrapper afterMassage={afterMassage}>
            <div className={cx('pain-level-box')}>
              <PainLevelRadio
                activeIndex={selectedPainLevel}
                onChange={index => {
                  setSelectedPainLevel(index);
                  handleScrollElement();
                }}
              />
            </div>
          </PainLevelWrapper>
        ) : (
          <>
            <PainLevelWrapper afterMassage={afterMassage} bodyPartLabel="肩部">
              <div className={cx('pain-level-box')}>
                <PainLevelRadio
                  activeIndex={selectedPainLevelByTwo?.shoulderPainLevel}
                  onChange={index => {
                    setSelectedPainLevelByTwo(preVal => {
                      let newVal = klona(preVal);
                      if (newVal === null) {
                        newVal = { waistPainLevel: null, shoulderPainLevel: null };
                      }
                      newVal.shoulderPainLevel = index;
                      return newVal;
                    });
                  }}
                />
              </div>
            </PainLevelWrapper>

            <PainLevelWrapper afterMassage={afterMassage} bodyPartLabel="腰部">
              <div className={cx('pain-level-box')}>
                <PainLevelRadio
                  activeIndex={selectedPainLevelByTwo?.waistPainLevel}
                  onChange={index => {
                    setSelectedPainLevelByTwo(preVal => {
                      let newVal = klona(preVal);
                      if (newVal === null) {
                        newVal = { waistPainLevel: null, shoulderPainLevel: null };
                      }
                      newVal.waistPainLevel = index;
                      return newVal;
                    });
                    handleScrollElement();
                  }}
                />
              </div>
            </PainLevelWrapper>
          </>
        )}

        <MotionTestCard
          beforeHandData={beforeHandData}
          actionTestItems={questionnaire?.action_test}
          onImgsChange={setActionTestImgs}
          afterMassage={afterMassage}
          afterWardsData={afterWardsData}
        />
      </div>
    );
  }
);

export default PictureContent;
